import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { nocService, updatesService } from '../services/apiService';
import PostCard from '../components/ui/post-card';
import { 
  DocumentIcon,
  GraduationCapIcon
} from '../components/common/Icons';
import { Pin, PinOff } from 'lucide-react';

const Dashboard = () => {
  const { user } = useAuth();
  const [activeNotifications, setActiveNotifications] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [pinnedNoticeData, setPinnedNoticeData] = useState(null);

  useEffect(() => {
    if (!user?.id) {
      setPinnedNoticeData(null);
      return;
    }
    const key = `pinnedNotice_${user.id}`;
    
    const checkPinnedNotice = () => {
      const str = localStorage.getItem(key);
      try {
        setPinnedNoticeData(str ? JSON.parse(str) : null);
      } catch (e) {
        setPinnedNoticeData(null);
      }
    };
    
    checkPinnedNotice();

    const handleStorageChange = (e) => {
      if (e.key === key) checkPinnedNotice();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('local-pin-change', checkPinnedNotice);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('local-pin-change', checkPinnedNotice);
    };
  }, [user?.id]);

  useEffect(() => {
    console.log('👤 Dashboard user data:', user);
    if (user?.userType === 'student') {
      fetchStatistics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadNotifications = async () => {
    if (!user) {
      setActiveNotifications([]);
      return;
    }

    try {
      setLoadingNotifications(true);
      const response = await updatesService.getUpdates();
      const archivedResponse = await updatesService.getArchivedUpdates();
      const list = Array.isArray(response?.data) ? response.data : [];
      const archivedList = Array.isArray(archivedResponse?.data) ? archivedResponse.data : [];
      const allNotices = [...list, ...archivedList].sort((a, b) => {
        const dateA = a.publishedAt ? new Date(a.publishedAt) : new Date(a.createdAt);
        const dateB = b.publishedAt ? new Date(b.publishedAt) : new Date(b.createdAt);
        return dateB - dateA;
      });

      if (user?.id) {
        const pinnedStr = localStorage.getItem(`pinnedNotice_${user.id}`);
        if (pinnedStr) {
          try {
            const pinnedData = JSON.parse(pinnedStr);
            const noticeIndex = allNotices.findIndex(n => n.id === pinnedData.notificationId);
            if (noticeIndex > -1) {
              const [pinnedItem] = allNotices.splice(noticeIndex, 1);
              allNotices.unshift(pinnedItem);
            } else {
              localStorage.removeItem(`pinnedNotice_${user.id}`);
              window.dispatchEvent(new Event('local-pin-change'));
            }
          } catch(e) {
            console.error('Failed to parse pinned notice', e);
          }
        }
      }

      setActiveNotifications(allNotices);
    } catch (_) {
      setActiveNotifications([]);
    } finally {
      setLoadingNotifications(false);
    }
  };

  useEffect(() => {
    if (!user) {
      setActiveNotifications([]);
      return;
    }

    loadNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchStatistics = async () => {
    try {
      await nocService.getNOCStatistics();
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    }
  };


  console.log('🔍 Dashboard Debug:');
  console.log('   User:', user);
  console.log('   User Type:', user?.userType);
  console.log('   User Role:', user?.role);

  const itemsPerPage = 5;
  const totalPages = Math.ceil(activeNotifications.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const displayedNotifications = activeNotifications.slice(startIndex, startIndex + itemsPerPage);

  return (
    <>
            {/* Welcome Banner */}
            <div className="bg-[#0f172a] rounded-3xl p-8 sm:p-12 mb-8 relative overflow-hidden shadow-xl text-white">
                <div className="relative z-10 max-w-2xl">
                    <span className="inline-block px-3 py-1 bg-amber-400 text-amber-900 text-xs font-bold rounded-full mb-4 uppercase tracking-wider">
                        {user?.userType === 'student' ? 'Student Profile' : 'Staff Dashboard'}
                    </span>
                    <h1 className="text-3xl sm:text-4xl font-bold mb-6">
                        Welcome back, {user?.name}!
                    </h1>
                    
                    <div className="flex flex-wrap gap-6 text-sm sm:text-base text-slate-300">
                        <div className="flex items-center gap-2">
                            <GraduationCapIcon className="w-5 h-5 text-amber-400" />
                            <span>
                                {user?.department}
                                {user?.semester && ` | ${user?.semester.replace('SEM_', 'Semester ')}`}
                            </span>
                        </div>
                        {user?.usn && (
                            <div className="flex items-center gap-2">
                                <DocumentIcon className="w-5 h-5 text-amber-400" />
                                <span>USN: {user?.usn}</span>
                            </div>
                        )}
                         <div className="flex items-center gap-2">
                            <svg className="w-5 h-5 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span>CIT North Campus</span>
                        </div>
                    </div>
                </div>
                {/* Decorative Circle */}
                <div className="absolute top-1/2 -right-12 -translate-y-1/2 w-80 h-80 bg-white/5 rounded-full blur-3xl"></div>
            </div>

            {/* Administrative Notices */}
            <div className="mb-10">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-2">
                        <h2 className="text-xl font-bold text-slate-800">Administrative Notices</h2>
                        <span className="w-2 h-2 rounded-full bg-red-500"></span>
                    </div>
                    <div className="flex gap-2 items-center">
                       
                        <span className="px-3 py-1 bg-slate-200 text-slate-600 text-xs font-bold rounded-full uppercase tracking-wider">
                            Updates
                        </span>
                    </div>
                </div>

                {pinnedNoticeData && (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      const noticeIndex = activeNotifications.findIndex(n => n.id === pinnedNoticeData.notificationId);
                      const targetPage = noticeIndex > -1 ? Math.floor(noticeIndex / itemsPerPage) + 1 : 1;
                      
                      if (currentPage !== targetPage) {
                        setCurrentPage(targetPage);
                        setTimeout(() => {
                          document.getElementById(`notice-${pinnedNoticeData.notificationId}`)?.scrollIntoView({ behavior: 'smooth' });
                        }, 100);
                      } else {
                        document.getElementById(`notice-${pinnedNoticeData.notificationId}`)?.scrollIntoView({ behavior: 'smooth' });
                      }
                    }}
                    className="w-full text-left mb-6 flex items-center justify-between bg-white text-slate-800 px-4 py-3 rounded-xl shadow-sm border border-slate-200 hover:bg-slate-50 transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center gap-3 overflow-hidden">
                      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                        <Pin size={16} className="text-slate-600 fill-slate-600" />
                      </div>
                      <span className="text-sm font-medium truncate">{pinnedNoticeData.title}</span>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (user?.id) {
                          localStorage.removeItem(`pinnedNotice_${user.id}`);
                          window.dispatchEvent(new Event('local-pin-change'));
                        }
                      }}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-200 transition-colors"
                      title="Unpin notice"
                    >
                      <PinOff size={16} />
                    </button>
                  </div>
                )}

                {loadingNotifications ? (
                    <div className="flex gap-6 overflow-x-auto pb-4">
                         {[1, 2].map(i => (
                             <div key={i} className="min-w-[350px] h-48 bg-white rounded-xl shadow-sm animate-pulse"></div>
                         ))}
                    </div>
                ) : activeNotifications.length > 0 ? (
                  <>
                    <div className="flex flex-col gap-6 w-full">
                      {displayedNotifications.map((notification) => (
                      <div key={notification.id} id={`notice-${notification.id}`} className="w-full scroll-mt-24">
                        <PostCard
                          title={notification.title}
                          content={notification.content}
                          imageUrl={notification.imageUrl}
                          authorName={notification.postedByName || 'Administration'}
                          authorMeta={notification.postedByRole ? `@${notification.postedByRole.toLowerCase()}` : '@administration'}
                          notificationId={notification.id}
                          initialLikes={notification.likes || 0}
                          initialDislikes={notification.dislikes || 0}
                          userReaction={notification.userReaction || null}
                        />
                      </div>
                      ))}
                    </div>

                    {totalPages > 1 && (
                      <div className="flex justify-center items-center gap-4 mt-4">
                        <button
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          className={`p-2 rounded-full ${currentPage === 1 ? 'text-slate-300 cursor-not-allowed' : 'text-slate-600 hover:bg-slate-100'}`}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                        </button>
                        <span className="text-sm font-medium text-slate-600">
                          Page {currentPage} of {totalPages}
                        </span>
                        <button
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          disabled={currentPage === totalPages}
                          className={`p-2 rounded-full ${currentPage === totalPages ? 'text-slate-300 cursor-not-allowed' : 'text-slate-600 hover:bg-slate-100'}`}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                    <div className="bg-white rounded-xl p-8 text-center border border-dashed border-slate-300">
                        <p className="text-slate-500">No new notices at the moment.</p>
                    </div>
                )}
            </div>


            {/* Academic Services - REMOVED */}

    </>
  );
};

export default Dashboard;
